import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { API_BASE } from '../../config';
import WorkspaceSidebarContext from '../WorkspaceSidebarContext';
import DeckWorkspaceShell from './DeckWorkspaceShell';
import FullPlanOverlay from './FullPlanOverlay';
import savedDeckLabel from './savedDeckLabel';
import '../DeckAssembler.linear.css';
import './MeetingDeckLibrary.css';

export default function MeetingDeckLibrary({ onStartChat, onOpenDeck }) {
  const { token } = useAuth();
  const enterWorkspaceSidebar = useContext(WorkspaceSidebarContext);
  useEffect(() => enterWorkspaceSidebar(), [enterWorkspaceSidebar]);
  const [chooserOpen, setChooserOpen] = useState(true);
  const [selected, setSelected] = useState('');
  const opener = useRef(null);
  const close = useCallback(() => setChooserOpen(false), []);
  const [decks, setDecks] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/api/decks/compositions`, {
      headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error('Saved decks could not be loaded. Refresh to try again.');
      const data = await response.json();
      if (!controller.signal.aborted) setDecks(data.compositions || []);
    }).catch(failure => {
      if (!controller.signal.aborted) setError(failure.message);
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token]);
  const choices = <div className="dk-library-dialog">
    <header><div><span className="dk-library-eyebrow">Your presentation workspace</span>
      <h1>Meeting Prep</h1></div><button type="button" className="dk-btn" onClick={close} aria-label="Close deck chooser">Close</button></header>
    <p className="dk-library-description">Start with a conversation, or pick up where you left off.</p>
    <section className="dk-library-new"><h2>Start a new deck</h2>
      <p>Tell Wingman who you’re meeting and what you want to cover.</p>
      <button type="button" className="dk-btn acc" onClick={onStartChat}>Start a deck in chat <span aria-hidden="true">→</span></button>
    </section>
    <section className="dk-library-saved"><h2>Open a saved deck</h2>
      {error && <p role="alert">{error}</p>}
      {loading ? <p role="status">Loading saved decks…</p> : !error && !decks.length ? <p>Your saved decks will appear here.</p> : null}
      {!loading && decks.length > 0 && <>
        <label className="dk-library-label" htmlFor="meeting-saved-deck">Saved decks</label>
        <select id="meeting-saved-deck" className="dk-inp sel" value={selected} onChange={event => setSelected(event.target.value)}>
          <option value="" disabled>Select a deck…</option>
          {decks.map(deck => <option key={deck.composition_id} value={deck.composition_id}>
            {savedDeckLabel(deck)}
          </option>)}
        </select>
        <button type="button" className="dk-btn" disabled={!selected} onClick={() => onOpenDeck(Number(selected))}>Open deck</button>
      </>}
    </section>
  </div>;
  return <section className="dk dk-v2 dk-normal-host dk-library" aria-label="Meeting Prep">
    <DeckWorkspaceShell notesOpen={false} onOpenNotes={() => {}}
      toolbar={<div className="dk-library-toolbar"><strong>Meeting Prep</strong><button ref={opener} type="button" className="dk-btn" onClick={() => setChooserOpen(true)}>Choose a deck</button></div>}
      rail={<div className="dk-library-rail"><strong>Pages</strong><p>Your slides appear here when you open a deck.</p></div>}
      notes={<div className="dk-library-notes">Speaker notes</div>}>
      <div className="dk-library-canvas"><svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true"><rect x="7" y="9" width="42" height="30" rx="4" stroke="currentColor" strokeWidth="1.5"/><path d="M28 39v9m-9 0h18M16 19h24M16 26h16" stroke="currentColor" strokeWidth="1.5"/></svg>
        <h2>Ready for your next meeting</h2><p>Open a saved presentation or start a new deck in chat.</p>
        <button type="button" className="dk-btn" onClick={() => setChooserOpen(true)}>Choose a deck</button>
      </div>
    </DeckWorkspaceShell>
    {chooserOpen && <FullPlanOverlay label="Choose a meeting deck" className="dk-library-overlay" onClose={close} returnFocusRef={opener}>{choices}</FullPlanOverlay>}
  </section>;
}
